import { useState, useRef } from "react";
import { formatPrice } from "../../utils/formatPrice";
import { Box, Tooltip, Typography } from "@mui/material";
import { Carousel } from "antd";
import coverImage from "../../assets/images/landscape-cover.jpg";
import Slider from "react-slick";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CarouselArrows from "./CarouselArrows";
import { getBoatStatus } from "../../utils/boatTypes";
import DoneIcon from "@mui/icons-material/Done";
import CircleIcon from "@mui/icons-material/Circle";

const BACKEND_URL = process.env.REACT_APP_API_URL || '';

const imageUrl = (originalUrl: string | undefined): string => {
  if (!originalUrl) return '';
  if (!originalUrl.includes('booking-manager.com')) return originalUrl;
  return `${BACKEND_URL}/api/image/?url=${encodeURIComponent(originalUrl)}&w=400&h=280&q=82`;
};

const PrevArrow = ({ onClick, className, style }: any) => (
  <Box
    className={className}
    onClick={onClick}
    sx={{ ...style, display: "flex !important", alignItems: "center", justifyContent: "center", "&::before": { display: "none" } }}
  >
    <ArrowBackIosIcon sx={{ color: "#333", fontSize: 18 }} />
  </Box>
);
const NextArrow = ({ onClick, className, style }: any) => (
  <Box
    className={className}
    onClick={onClick}
    sx={{ ...style, display: "flex !important", alignItems: "center", justifyContent: "center", "&::before": { display: "none" } }}
  >
    <ArrowForwardIosIcon sx={{ color: "#333", fontSize: 18 }} />
  </Box>
);

const SimilatBoatsWidget = (props: any) => {
  const { similarBoats, handleYachtClick } = props;
  const [isHovered, setIsHovered] = useState<number | null>(null);
  const carouselRefs = useRef<any>([]);

  var settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    initialSlide: 0,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    centerMode: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          centerMode: false,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          centerMode: false,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: false,
        },
      },
    ],
  };

  return (
    <Slider {...settings}>
      {similarBoats?.map((boat: any, index: number) => {
        const statusInfo = getBoatStatus(boat?.status);
        return (
          <Box
            key={index}
            display="flex"
            flexDirection="column"
            sx={{ p: 1, cursor: "pointer" }}
            onClick={() => handleYachtClick(boat)}
            onMouseEnter={() => setIsHovered(index)}
            onMouseLeave={() => setIsHovered(null)}
          >
            <Box
              sx={{
                position: "relative",
                borderRadius: "10px",
                overflow: "hidden",
                margin: "0 10px",
                border: "1px solid #d3d3d3",
                "& .slick-slide > div": { width: "100%" },
              }}
            >
              <Carousel
                ref={(ref) => (carouselRefs.current[index] = ref)}
                arrows={false}
                infinite={true}
                dots={false}
                speed={300}
              >
                {boat?.yacht?.images?.length > 0 ? (
                  [...(boat?.yacht?.images || [])]
                    .sort((a: any, b: any) => {
                      if (a.description === "Main image") return -1;
                      if (b.description === "Main image") return 1;
                      return 0;
                    })
                    .map((image: any, imgIndex: number) => (
                      <div key={imgIndex}>
                        <img
                          src={imageUrl(image?.url)}
                          alt={`Boat ${boat?.id}`}
                          style={{ width: "100%", height: "200px", objectFit: "cover", display: "block" }}
                        />
                      </div>
                    ))
                ) : (
                  <div>
                    <img
                      src={coverImage}
                      alt={`Boat ${boat?.id}`}
                      style={{ width: "100%", height: "200px", objectFit: "cover", display: "block" }}
                    />
                  </div>
                )}
              </Carousel>

              <CarouselArrows
                onPrevClick={(e: any) => {
                  e.stopPropagation();
                  carouselRefs.current[index]?.prev();
                }}
                onNextClick={(e: any) => {
                  e.stopPropagation();
                  carouselRefs.current[index]?.next();
                }}
                show={boat?.yacht?.images?.length > 1 && isHovered === index}
              />
            </Box>

            <Box
              sx={{ p: 1, cursor: "pointer" }}
              onClick={() => handleYachtClick(boat)}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontWeight: "bold",
                  flexWrap: "wrap",
                  gap: 0.5,
                }}
              >
                <Typography fontWeight="bold">
                  {boat?.yacht?.model || "N/A"} -{" "}
                </Typography>
                <Typography fontWeight="bold">
                  {boat?.location?.name || "N/A"} -{" "}
                </Typography>
                <Typography fontWeight="bold">
                  {boat?.location?.country || "N/A"}
                </Typography>
                <Tooltip
                  title={
                    <Box sx={{ padding: "5px" }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "16px",
                          mb: 0.5,
                        }}
                      >
                        {statusInfo.title}
                      </Typography>
                      <Typography sx={{ fontSize: "14px" }}>
                        {statusInfo.description}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top-start" // anchor to start of icon
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "#fff",
                        color: "#393c3d",
                        border: "1px solid #e0e0e0",
                        borderRadius: "12px",
                        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                        width: "220px",
                        maxWidth: "none",
                        whiteSpace: "normal",
                      },
                    },
                    arrow: {
                      sx: {
                        color: "#fff",
                        fontSize: 20,
                        "&::before": {
                          filter: "drop-shadow(0px 0px 1px rgba(0,0,0,0.50))",
                        },
                      },
                    },
                  }}
                >
                  {statusInfo.title === "Confirmed Availability" ? (
                    <DoneIcon
                      sx={{
                        color: "green",
                        height: "25px",
                        cursor: "pointer",
                      }}
                    />
                  ) : (
                    <CircleIcon
                      sx={{
                        color: statusInfo.color,
                        height: "14px",
                        cursor: "pointer",
                      }}
                    />
                  )}
                </Tooltip>
              </Box>
              <Typography fontSize="15px">{`${boat?.yacht?.cabins} cabins | ${boat?.yacht?.berths} beds | year ${boat?.yacht?.year}`}</Typography>
              <Box display="flex" alignItems="center" gap={1} pt={0.5} flexWrap="wrap">
                <Typography fontWeight="bold" fontSize="15px">
                  {formatPrice(boat?.actualPrice || boat?.actualStartPrice)}
                </Typography>
                {boat?.discountPercentage > 0 && boat?.actualStartPrice && boat?.actualStartPrice !== boat?.actualPrice && (
                  <Typography fontSize="13px" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                    {formatPrice(boat?.actualStartPrice)}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Slider>
  );
};

export default SimilatBoatsWidget;
